import React, {FC, useContext, useState} from "react";
import {Input, Modal, Typography, App} from "antd";
import {useTranslation} from "react-i18next";
import {AuthContext} from "../Contexts/AuthContext";
import {authorizationHeaders} from "../api/authorization";

type Props = {open: boolean; onClose: () => void};

export const DeveloperFeedbackDialog: FC<Props> = ({open, onClose}) => {
    const {message} = App.useApp();
    const {t} = useTranslation();
    const {auth} = useContext(AuthContext);
    const [description, setDescription] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const close = () => {
        if (submitting) return;
        setDescription("");
        onClose();
    };

    const submit = async () => {
        const report = description.trim();
        if (!report || submitting) return;

        setSubmitting(true);
        try {
            const response = await fetch("/api/organizer/feedback", {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    ...authorizationHeaders(auth.token)
                },
                body: JSON.stringify({message: report})
            });
            if (!response.ok) throw new Error("Report could not be queued.");

            message.success(t("developer-feedback-queued", {
                defaultValue: "Your report was received and is waiting to be sent."
            }));
            setDescription("");
            onClose();
        } catch {
            message.error(t("developer-feedback-error", {
                defaultValue: "Your report could not be sent right now. Please try again."
            }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open={open}
            title={t("developer-feedback-title", {defaultValue: "Report a problem"})}
            okText={t("developer-feedback-send", {defaultValue: "Send report"})}
            okButtonProps={{disabled: !description.trim() || submitting}}
            confirmLoading={submitting}
            onOk={() => void submit()}
            onCancel={close}
            destroyOnHidden
        >
            <Typography.Paragraph>
                {t("developer-feedback-help", {
                    defaultValue: "Describe what happened. Your name and email will be included for the development team."
                })}
            </Typography.Paragraph>
            <Input.TextArea
                aria-label={t("developer-feedback-description", {defaultValue: "Problem description"})}
                value={description}
                onChange={event => setDescription(event.target.value)}
                rows={6}
                maxLength={4000}
                showCount
                placeholder={t("developer-feedback-placeholder", {
                    defaultValue: "What were you trying to do, and what happened?"
                })}
            />
        </Modal>
    );
};
